import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { consignmentApi, type ConsignmentPositionLine } from '@/api/services';

/**
 * Agreeing the levels a node is run to.
 *
 * These are a contractual term, not a calculation — which is the whole reason
 * this dialog exists. The column was called `calculatedMinLevel` and nothing
 * ever calculated it; the only values in it were seeded, and the dashboard then
 * described them as agreed with the customer. So the act of setting them is
 * recorded with who did it and why.
 *
 * Clearing both is offered deliberately. "No level agreed" is a real state — the
 * one every new arrangement starts in — and it has to be reachable, or the only
 * way out of a wrong number is a different wrong number.
 */
export function AgreeLevelsDialog({
  line,
  locationId,
  onClose,
  onSaved,
}: {
  line: ConsignmentPositionLine | null;
  locationId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!line) return;
    setMin(line.minLevel != null ? String(line.minLevel) : '');
    setMax(line.maxLevel != null ? String(line.maxLevel) : '');
    setNote(line.levelsNote ?? '');
  }, [line]);

  if (!line) return null;

  const minNum = min.trim() === '' ? null : Number(min);
  const maxNum = max.trim() === '' ? null : Number(max);
  const clearing = minNum == null && maxNum == null;

  const problems: string[] = [];
  if (min.trim() !== '' && (!Number.isInteger(minNum) || (minNum as number) < 0))
    problems.push('The minimum must be a whole number of units, or blank.');
  if (max.trim() !== '' && (!Number.isInteger(maxNum) || (maxNum as number) < 0))
    problems.push('The maximum must be a whole number of units, or blank.');
  if (minNum != null && maxNum != null && minNum > maxNum)
    problems.push(`A minimum of ${minNum} cannot sit above a maximum of ${maxNum}.`);

  const save = async () => {
    setSaving(true);
    try {
      await consignmentApi.setStockingLevels(locationId, {
        itemId: line.itemId,
        minLevel: minNum,
        maxLevel: maxNum,
        note: note.trim() || undefined,
      });
      toast.success(
        clearing ? `${line.sku}: no level agreed` : `${line.sku}: levels agreed`
      );
      onClose();
      await onSaved();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not record those levels';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!line} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agreed levels — {line.sku}</DialogTitle>
          <DialogDescription>
            What this node is stocked to under the arrangement. Recorded against your name, because
            these are terms somebody agreed rather than a figure the system worked out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="min-level">Minimum</Label>
              <Input
                id="min-level"
                type="number"
                min={0}
                value={min}
                onChange={(e) => setMin(e.target.value)}
                placeholder="none"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="max-level">Maximum</Label>
              <Input
                id="max-level"
                type="number"
                min={0}
                value={max}
                onChange={(e) => setMax(e.target.value)}
                placeholder="none"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="levels-note">Why</Label>
            <Textarea
              id="levels-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="The contract clause, the lead time, the argument."
            />
            <p className="text-xs text-muted-foreground">
              A minimum held because the contract says so is a different thing from one held
              because the lead time is twelve weeks.
            </p>
          </div>

          {line.levelsAgreedBy && (
            <p className="text-xs text-muted-foreground">
              Currently {line.minLevel} / {line.maxLevel}, agreed by {line.levelsAgreedBy}
              {line.levelsAgreedAt
                ? ` on ${new Date(line.levelsAgreedAt).toLocaleDateString()}`
                : ''}
              .
            </p>
          )}

          {clearing && (
            <p className="text-xs text-status-warning-foreground">
              Leaving both blank records that no level is agreed. Nothing will be able to say
              whether this node is short, which is different from agreeing a minimum of zero.
            </p>
          )}

          {problems.length > 0 && (
            <ul className="space-y-0.5 text-xs text-status-destructive-foreground">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || problems.length > 0}>
            {saving ? 'Saving…' : clearing ? 'Record as not agreed' : 'Record agreed levels'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
